import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export const CENSORED_BIRTHDATE = '**/**/****'

export const isCensoredBirthdate = (date?: Date | string | null): date is typeof CENSORED_BIRTHDATE =>
  date === CENSORED_BIRTHDATE

export const getFormattedDate = (date?: Date | string | null, timezone?: string) => {
  if (!date) return ''
  if (isCensoredBirthdate(date)) return CENSORED_BIRTHDATE
  const navigatorLanguage = navigator.language
  const options: Intl.DateTimeFormatOptions = timezone ? { timeZone: timezone } : {}
  return new Date(date).toLocaleDateString(navigatorLanguage, options)
}

export const getFormattedDateWithHours = (
  t: any,
  date?: Date | string | null,
  displaySeconds = false,
  timezone?: string,
) => {
  if (!date) return ''
  const localeDate = getFormattedDate(date, timezone)
  const timeFormat = displaySeconds ? 'HH:mm:ss' : 'HH:mm'
  const dayjsDate = timezone ? dayjs(date).tz(timezone) : dayjs(date)
  const time = dayjsDate.format(timeFormat)
  return t('common.date-and-time', { date: localeDate, time })
}

/**
 * Formats a start/end period:
 * - no end: "DD/MM/YYYY à HH:mm"
 * - same day: "DD/MM/YYYY de HH:mm → HH:mm"
 * - different days: "du DD/MM/YYYY à HH:mm → DD/MM/YYYY à HH:mm"
 * Days are compared in the given timezone.
 */
export const getFormattedPeriod = (
  t: any,
  start?: Date | string | null,
  end?: Date | string | null,
  timezone?: string,
): string => {
  if (!start) return ''
  const toDayjs = (date: Date | string) => (timezone ? dayjs(date).tz(timezone) : dayjs(date))
  if (!end || !toDayjs(end).isValid()) return getFormattedDateWithHours(t, start, false, timezone)

  const startDate = toDayjs(start)
  const endDate = toDayjs(end)
  if (startDate.format('YYYY-MM-DD') === endDate.format('YYYY-MM-DD')) {
    return t('common.date-period-same-day', {
      date: getFormattedDate(start, timezone),
      start: startDate.format('HH:mm'),
      end: endDate.format('HH:mm'),
    })
  }
  return t('common.date-period-different-days', {
    start: getFormattedDateWithHours(t, start, false, timezone),
    end: getFormattedDateWithHours(t, end, false, timezone),
  })
}

export const getHoursMinutesAndSeconds = (date?: Date | string | null, timezone?: string) => {
  if (!date) return ''
  const dayjsDate = timezone ? dayjs(date).tz(timezone) : dayjs(date)
  const milliseconds = dayjsDate.format('SSS')
  const format = milliseconds === '000' ? 'HH:mm:ss' : 'HH:mm:ss.SSS'
  return dayjsDate.format(format)
}

export const getReadableDateWithHours = (t: any, givenDate?: Date | string, timezone?: string) => {
  if (!givenDate) return ''
  const dayjsDate = timezone ? dayjs(givenDate).tz(timezone) : dayjs(givenDate)
  const date = dayjsDate.format('D MMMM')
  const time = dayjsDate.format('HH:mm')
  return t('common.date-and-time', { date, time })
}

/**
 * Returns internationally understandable datetime string.
 * The string is sortable because it adheres to a lexicographical order that
 * aligns with the chronological order of dates and times.
 *
 * @param {Date | string} date
 * @returns {string}
 */
export const getInternationalSortableDateTimeString = (date?: Date | string, timezone?: string) => {
  const dayjsDate = timezone ? dayjs(date).tz(timezone) : dayjs(date)
  return dayjsDate.format('YYYY/MM/DD HH[h]mm')
}

export const getInternationalSortableDateTimeSecondsString = (date?: Date | string, timezone?: string) => {
  const dayjsDate = timezone ? dayjs(date).tz(timezone) : dayjs(date)
  return dayjsDate.format('YYYY/MM/DD HH[h]mm:ss.SSS')
}

export const getDayDiff = (date1: Date, date2: Date): number => {
  const time1 = date1.getTime()
  const time2 = date2.getTime()
  return Math.floor((time2 - time1) / (24 * 3600 * 1000))
}

export const formatAllDatesWithHoursInObject = (t: any, obj: any, timezone?: string): any => {
  if (typeof obj === 'string') {
    // Rely on regex for format validation because dayjs has an old bug (https://github.com/iamkun/dayjs/issues/929)
    // preventing usage of `strict` mode for date parsing with timezones
    // This regex also ensure we don't try to parse dates from random strings (dayjs considers '1234' as a valid date)
    const isValidDateStringFormat =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|z|[+-]\d{2}:\d{2})$/.test(obj)
    if (!isValidDateStringFormat) {
      return obj
    }
    const date = dayjs(obj, ['YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DDTHH:mm:ssZ'])
    return getFormattedDateWithHours(t, date.toDate(), false, timezone)
  } else if (Array.isArray(obj)) {
    return obj.map((item) => formatAllDatesWithHoursInObject(t, item, timezone))
  } else if (obj instanceof Date) {
    return obj
  } else if (typeof obj === 'object' && obj !== null) {
    // Returns a copy: callers share these objects (e.g. react-query data) and must not see them mutated
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, formatAllDatesWithHoursInObject(t, value, timezone)]),
    )
  }
  return obj
}
